/**
 * Structured ingest — the review of a table before it enters the case.
 *
 * One card per table, top to bottom: the mapping drawn beside the mapping assistant; what
 * enters the case, with each entity's identifier in view; what to look at before adding;
 * and the button whose label says exactly what it will write. Reference material (the
 * columns, fields read from text) folds away at the bottom. Pure: state in, actions out.
 */

import { Loader2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { TooltipProvider } from '@/components/ui/tooltip'
import {
  isProjecting,
  type Preview,
  type ProjectionResult,
  type Proposal,
  previewLeftOut,
  type RowExplanation,
  type RowsPage,
  type RowsQuery,
  type StructuredIngestAnalysis,
  type StructuredIngestAnalysisSummary,
  type StructuredIngestTable,
  summarizeOutput,
  unacceptedNewTypes,
} from '@/services/api/structuredIngest'
import { DerivedColumns } from './DerivedColumns'
import { FileMasthead } from './FileMasthead'
import { MappingOutline } from './MappingOutline'
import { MappingWorkspace } from './MappingWorkspace'
import { ReviewPanel } from './ReviewPanel'
import { RowsDialog, type RowsSelection } from './RowsDialog'
import { RowTextExtractionOptions } from './RowTextExtractionOptions'
import { ColumnsTable, Fold, NullTokensInput } from './recipe-cards'
import { withEnabled, withWindows } from './recipe-edits'
import { reviewState } from './review-state'
import { SchemaBand } from './SchemaBand'
import { SchemaIdentityProvider } from './schema-identity'
import { TableHeader } from './TableHeader'
import { TextExtractionOptions } from './TextExtractionOptions'
import { hasTextExtraction } from './text-extraction'
import { UnmappedColumns } from './UnmappedColumns'
import { WriteBar } from './WriteBar'

export interface RowsView {
  open: boolean
  title: string
  subtitle?: string | null
  page: RowsPage | null
  loading: boolean
  error: string | null
  /** Marking rows to promote — null when the rows are read-only. */
  selection?: RowsSelection | null
}

/** The row under the lens — one table at a time. */
export interface LensView {
  tableId: string | null
  position: number
  explanation: RowExplanation | null
  loading: boolean
}

export interface StructuredIngestState {
  analysis: StructuredIngestAnalysis | null
  loading?: boolean
  loadError?: string | null
  analyses: StructuredIngestAnalysisSummary[]
  /** Edited recipe per tableId — starts as the model's proposal (or the last confirmed recipe). */
  recipes: Record<string, Proposal>
  /** Recomputed preview per tableId once the recipe was edited. */
  previews: Record<string, Preview>
  previewing: string | null
  projecting: string | null
  reannotating: string | null
  /** The whole file is being read again — a new analysis is on its way. */
  redoingFile?: boolean
  rows: RowsView
  lens: LensView
}

export interface StructuredIngestActions {
  onOpenAnalysis: (analysisId: string) => void
  onRetry?: () => void
  /** Tables enter the case through the Documents page — take the investigator there, or to one document. */
  onOpenDocuments: (documentId?: string) => void
  onRecipeChange: (tableId: string, recipe: Proposal) => void
  onProject: (tableId: string) => void
  onReannotate: (tableId: string, instructions: string) => Promise<boolean>
  /** Read the file again from the start — new tables, new proposals, as a new analysis. */
  onRedoFile: (instructions: string) => Promise<boolean>
  onShowRows: (tableId: string, query: RowsQuery, title: string, subtitle?: string) => void
  onRowsPage: (offset: number) => void
  onCloseRows: () => void
  /** Put a row under the lens (tableId null = lens off). */
  onLensChange: (tableId: string | null, position: number) => void
}

interface StructuredIngestViewProps {
  state: StructuredIngestState
  actions: StructuredIngestActions
}

function PageStates({ state, onRetry }: { state: StructuredIngestState; onRetry?: () => void }) {
  const { t } = useTranslation()
  const analysis = state.analysis
  return (
    <>
      {!analysis && !state.loading && !state.loadError && (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            {t('tableMapping.page.empty')}
          </CardContent>
        </Card>
      )}
      {(state.loading || (analysis?.status === 'pending' && !analysis.tables.length)) && (
        <Card>
          <CardContent className="flex items-center gap-3 py-5 text-sm">
            <Loader2 aria-hidden className="size-4 animate-spin motion-reduce:animate-none" />
            {t('tableMapping.page.preparing', {
              file: analysis?.filename ?? t('tableMapping.page.thisFile'),
            })}
          </CardContent>
        </Card>
      )}
      {(state.loadError || analysis?.status === 'failed') && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 py-5 text-sm text-destructive">
            {t('tableMapping.page.failed', { error: state.loadError ?? analysis?.error ?? '' })}
            {state.loadError && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                {t('tableMapping.page.retry')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </>
  )
}

export function StructuredIngestView({ state, actions }: StructuredIngestViewProps) {
  const analysis = state.analysis
  // One mapping at a time per file: a running proposal, table redo or whole-file redo
  // blocks edits and further re-runs.
  const mappingLocked =
    !!analysis &&
    (analysis.status === 'pending' ||
      analysis.tables.some(item => item.status === 'pending') ||
      state.reannotating !== null ||
      !!state.redoingFile)

  return (
    <SchemaIdentityProvider value={analysis?.schemaInfo?.propertyUniqueness ?? {}}>
      <TooltipProvider delayDuration={150}>
        <div className="flex flex-col gap-6 pb-16">
          <div className="flex flex-col gap-3">
            <FileMasthead
              analysis={analysis}
              analyses={state.analyses}
              locked={mappingLocked}
              actions={actions}
            />
            {analysis?.schemaInfo && <SchemaBand info={analysis.schemaInfo} />}
          </div>
          <PageStates state={state} onRetry={actions.onRetry} />
          {analysis?.tables.map(table => (
            <TableSection
              key={`${analysis.analysisId}/${table.tableId}`}
              table={table}
              caseContext={analysis.caseContext ?? null}
              schemaLabels={analysis.schemaLabels}
              recipe={
                state.recipes[table.tableId] ??
                table.reviewRecipe ??
                table.confirmed?.recipe ??
                table.proposal
              }
              preview={state.previews[table.tableId] ?? table.preview}
              busy={{
                projecting: state.projecting === table.tableId || isProjecting(table),
                reannotating: state.reannotating === table.tableId || table.status === 'pending',
                previewing: state.previewing === table.tableId,
                mappingLocked,
              }}
              result={table.projection ?? null}
              lens={state.lens.tableId === table.tableId ? state.lens : null}
              actions={actions}
            />
          ))}
          <RowsDialog
            open={state.rows.open}
            title={state.rows.title}
            subtitle={state.rows.subtitle}
            page={state.rows.page}
            loading={state.rows.loading}
            error={state.rows.error}
            selection={state.rows.selection ?? null}
            onOpenChange={open => {
              if (!open) actions.onCloseRows()
            }}
            onPage={actions.onRowsPage}
          />
        </div>
      </TooltipProvider>
    </SchemaIdentityProvider>
  )
}

// --- one table --------------------------------------------------------------------------

interface TableSectionProps {
  table: StructuredIngestTable
  caseContext: string | null
  schemaLabels: string[]
  recipe: Proposal
  preview: Preview
  busy: { projecting: boolean; reannotating: boolean; previewing: boolean; mappingLocked: boolean }
  result: ProjectionResult | null
  lens: LensView | null
  actions: StructuredIngestActions
}

/** Reference material under the review: the columns as read, and fields read from text. */
function TableDetails({
  table,
  recipe,
  preview,
  onChange,
}: {
  table: StructuredIngestTable
  recipe: Proposal
  preview: Preview
  onChange: (recipe: Proposal) => void
}) {
  const { t } = useTranslation()
  const derived = recipe.derived_columns ?? []
  return (
    <div className="flex flex-col gap-3">
      <Fold title={t('tableMapping.details.columns', { count: table.facts.columns.length })}>
        <div className="flex flex-col gap-3">
          <NullTokensInput
            value={recipe.null_tokens ?? []}
            onChange={tokens => onChange({ ...recipe, null_tokens: tokens })}
          />
          <ColumnsTable table={table} recipe={recipe} />
        </div>
      </Fold>
      {derived.length > 0 && (
        <Fold title={t('tableMapping.details.derived', { count: derived.length })}>
          <DerivedColumns
            columns={derived}
            preview={preview.derived_columns ?? []}
            rowsTotal={table.facts.rows}
          />
        </Fold>
      )}
    </div>
  )
}

function TableSection({
  table,
  caseContext,
  schemaLabels,
  recipe,
  preview: rawPreview,
  busy,
  result,
  lens,
  actions,
}: TableSectionProps) {
  const { t } = useTranslation()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const workspaceRef = useRef<HTMLDivElement | null>(null)
  const facts = table.facts
  const pending = busy.reannotating
  const update = (next: Proposal) => actions.onRecipeChange(table.tableId, next)

  const preview = useMemo<Preview>(
    () => ({ descriptors: [], relationships: [], warnings: [], ...rawPreview }),
    [rawPreview]
  )
  const summary = useMemo(() => summarizeOutput(recipe, preview), [recipe, preview])
  const review = useMemo(
    () => reviewState(recipe, preview, summary, unacceptedNewTypes(recipe)),
    [recipe, preview, summary]
  )
  const confirmedRecipe = table.confirmed?.recipe
  const changedSinceWrite = useMemo(
    () => !!confirmedRecipe && JSON.stringify(confirmedRecipe) !== JSON.stringify(recipe),
    [confirmedRecipe, recipe]
  )

  // A new proposal may rename entities. Keep the selection on a valid item.
  useEffect(() => {
    const items = [...summary.nodes, ...summary.edges]
    if (selectedId && !items.some(item => item.name === selectedId)) setSelectedId(null)
  }, [selectedId, summary])
  const inspect = (id: string) => {
    setSelectedId(id)
    workspaceRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }

  return (
    <Card className="rounded-2xl border-border/60 bg-card/70 shadow-sm">
      <TableHeader
        data={{
          table,
          recipe,
          caseContext,
          written: result,
          totals: summary.totals,
          changedSinceWrite,
          pending,
        }}
        onOpenRows={() => actions.onShowRows(table.tableId, {}, facts.name)}
      />
      <CardContent className="flex flex-col gap-8 px-4 md:px-6">
        <MappingWorkspace
          data={{ table, recipe, preview, summary, schemaLabels, lens, busy }}
          selection={{ id: selectedId, onSelect: setSelectedId, container: workspaceRef }}
          actions={actions}
        />
        {!pending && (
          <>
            <MappingOutline
              data={{ summary, review, rowsTotal: facts.rows, updating: busy.previewing }}
              selection={{ id: selectedId, onSelect: inspect }}
              actions={{
                onToggle: (name, enabled) => update(withEnabled(recipe, name, enabled)),
                onToggleWindows: enabled => update(withWindows(recipe, enabled)),
              }}
            />
            <UnmappedColumns
              columns={preview.unmapped_columns ?? []}
              total={facts.columns.length}
            />
            <div className="flex flex-col gap-4">
              <TextExtractionOptions
                recipe={recipe}
                disabled={busy.projecting || busy.previewing}
                onChange={update}
              />
              <RowTextExtractionOptions
                recipe={recipe}
                columns={facts.columns}
                disabled={busy.projecting || busy.previewing}
                onChange={update}
              />
              <ReviewPanel
                data={{ recipe, preview, summary, review }}
                disabled={busy.projecting || busy.previewing}
                actions={{ onChange: update, onInspect: inspect }}
              />
              <WriteBar
                data={{
                  written: result,
                  writtenAt: table.confirmed?.at ?? null,
                  leftOut: previewLeftOut(preview),
                  summary,
                  review,
                  extraction: {
                    selected: hasTextExtraction(recipe),
                    cells: preview.extraction_cells ?? null,
                  },
                  projecting: busy.projecting,
                  pending: pending || table.status === 'failed' || busy.previewing,
                  runError:
                    table.projectionRun?.status === 'failed'
                      ? (table.projectionRun.error ?? t('tableMapping.write.unknownError'))
                      : null,
                }}
                onProject={() => actions.onProject(table.tableId)}
              />
            </div>
            <TableDetails table={table} recipe={recipe} preview={preview} onChange={update} />
          </>
        )}
      </CardContent>
    </Card>
  )
}
